import { z } from 'zod'

// Base schemas for measurement JSON format
const MeasurementJsonSchema = z.object({
  num: z.number(),
  unit: z.string(),
})

const NullableMeasurementJsonSchema = MeasurementJsonSchema.nullable()

// User parameters schema
const UserParamsSchema = z.object({
  fca: NullableMeasurementJsonSchema,
  fcaMl: NullableMeasurementJsonSchema,
  corrosionRate: NullableMeasurementJsonSchema,
  corrosionRateMl: NullableMeasurementJsonSchema,
  targetDate: z.string().nullable(),
  inspectionDate: z.string().nullable(),
  selectedMawpCalcThickness: z.enum(['t_mm', 't_am', 't_c']).nullable(),
})

// Data values schema (measurements + boolean)
const DataValuesSchema = z.object({
  tmm: NullableMeasurementJsonSchema,
  trd: NullableMeasurementJsonSchema,
  tc: NullableMeasurementJsonSchema,
  longitudinalFlawLength: NullableMeasurementJsonSchema,
  latitudinalFlawLength: NullableMeasurementJsonSchema,
  innerDiameter: NullableMeasurementJsonSchema,
  outerDiameter: NullableMeasurementJsonSchema,
  lambda: NullableMeasurementJsonSchema,
  nominal: NullableMeasurementJsonSchema,
  t_ml: NullableMeasurementJsonSchema, // Part 4 specific
  d_ml: NullableMeasurementJsonSchema, // Part 4 specific
  corrosionOnInnerSurface: z.boolean(),
})

// Part 5 specific: Flaw size criteria schema
const FlawSizeCriteriaSchema = z.object({
  rt: NullableMeasurementJsonSchema,
  tmm_threshold: NullableMeasurementJsonSchema,
  lmsd: NullableMeasurementJsonSchema,
  lmsd_threshold: NullableMeasurementJsonSchema,
  remainingThicknessThresholdRatio: NullableMeasurementJsonSchema,
})

// Part 4 specific: Calculated values schema
const CalculatedValuesSchema = z.object({
  ts_am: NullableMeasurementJsonSchema,
  tc_am: NullableMeasurementJsonSchema,
  rt: NullableMeasurementJsonSchema,
  rsf_a: NullableMeasurementJsonSchema,
  q: NullableMeasurementJsonSchema,
  l: NullableMeasurementJsonSchema,
})

// Part 4 specific: Thickness criteria schema
const ThicknessCriteriaSchema = z.object({
  t_lim: NullableMeasurementJsonSchema,
  t_c_min: NullableMeasurementJsonSchema,
  t_l_min: NullableMeasurementJsonSchema,
  thickness_criteria_min: NullableMeasurementJsonSchema,
  effective_measured_thickness: NullableMeasurementJsonSchema,
})

// Final acceptability criteria schema
const FinalAcceptabilityCriteriaSchema = z.object({
  rsf: NullableMeasurementJsonSchema,
  rsf_a: NullableMeasurementJsonSchema,
  mt: NullableMeasurementJsonSchema,
  rt_threshold: NullableMeasurementJsonSchema,
})

// MAWP schema
const MawpSchema = z.object({
  e: NullableMeasurementJsonSchema,
  y: NullableMeasurementJsonSchema,
  w: NullableMeasurementJsonSchema,
  s_y: NullableMeasurementJsonSchema,
  tsl: NullableMeasurementJsonSchema,
  mawp_c: NullableMeasurementJsonSchema,
  mawp_l: NullableMeasurementJsonSchema,
  mawp_design: NullableMeasurementJsonSchema,
  mawpr: NullableMeasurementJsonSchema,
  calcType: z.enum(['thinWall', 'thickWall']).nullable().optional(),
})

// Outcome schema
const OutcomeSchema = z.object({
  result: z.enum(['pass', 'derate', 'fail', 'pending', 'skipped']),
  recommendation: z.string(),
  inspector: z.string(),
  scenario: z.string(),
  assumptions: z.string().default(''),
  conclusion: z.string().default(''),
})

// Base schema with common fields
const BaseReportDataSchema = z.object({
  user_params: UserParamsSchema,
  data_values: DataValuesSchema,
  mawp: MawpSchema,
  outcome: OutcomeSchema,
  ffs_level: z.enum(['1', '2']),
  is_default: z.boolean().default(false).optional(),
})

// Part 4 specific schema
export const FfsPartFourReportDataSchema = BaseReportDataSchema.extend({
  ffs_part: z.literal('part-4'),
  calculated_values: CalculatedValuesSchema,
  thickness_criteria: ThicknessCriteriaSchema,
})

// Part 5 specific schema
export const FfsPartFiveReportDataSchema = BaseReportDataSchema.extend({
  ffs_part: z.literal('part-5'),
  flaw_size_criteria: FlawSizeCriteriaSchema,
  final_acceptability_criteria: FinalAcceptabilityCriteriaSchema,
})

// Union schema for validation
export const FfsLtaReportDataSchema = z.union([
  FfsPartFourReportDataSchema,
  FfsPartFiveReportDataSchema,
])

// Complete RolodexEntry data schema
export const FfsLtaRolodexEntrySchema = z.object({
  id: z.string(),
  data: FfsLtaReportDataSchema,
})

// Export inferred types
export type FfsLtaReportData = z.infer<typeof FfsLtaReportDataSchema>
export type FfsLtaRolodexEntry = z.infer<typeof FfsLtaRolodexEntrySchema>
export type MeasurementJson = z.infer<typeof MeasurementJsonSchema>
export type NullableMeasurementJson = z.infer<
  typeof NullableMeasurementJsonSchema
>

// Validation helper function
export function validateFfsLtaRolodexEntry(data: unknown): FfsLtaRolodexEntry {
  try {
    return FfsLtaRolodexEntrySchema.parse(data)
  } catch (error) {
    if (error instanceof z.ZodError) {
      throw new Error(`Invalid FFS LTA report data: ${error.message}`)
    }
    throw error
  }
}
